import React from 'react'
import * as ROUTES from 'constants/routes'
import {
  Box,
  Button,
  Flex,
  Link,
  Loading,
  Message,
  Text,
  Table,
  Tooltip,
  useAppTheme,
} from '@workwhile/ui'
import { ColumnDef } from '@tanstack/react-table'
import { formatDistanceStrict } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import { Info, TriangleAlert } from 'lucide-react'
import { TableEmptyState } from 'components/Table/TableEmptyState'
import { getLocalTimezone } from 'lib/time'
import { usePendingApprovalsContextValue } from '../context/PendingApprovalsProvider'

type TableState = ReturnType<
  typeof usePendingApprovalsContextValue
>['state']['upcoming']

type AssessmentRow = TableState['items'][number]

type PendingApprovalsTableProps = {
  table: TableState
  positionId: number
  onLoadMore: () => void
}

const ONE_HOUR_MS = 60 * 60 * 1000

const SHIFT_STARTS_HEADER_TOOLTIP =
  'Hover a line item to see the full timestamp.'

const SHIFT_STARTS_URGENT_TOOLTIP =
  'The next shift starts very soon, but this worker might have applied for later shifts as well. Click "View" to see more information'

const formatTimestampWithTimezone = (date: Date) =>
  formatInTimeZone(date, getLocalTimezone(), 'MMM dd, yyyy, hh:mm a zzz')

const ShiftStartsHeader = () => (
  <Flex alignItems="center" gap={1}>
    <Text>Shift starts</Text>
    <Tooltip content={SHIFT_STARTS_HEADER_TOOLTIP}>
      <Info size={14} />
    </Tooltip>
  </Flex>
)

const ShiftStartsCell = ({
  shiftStartsAt,
  now,
}: {
  shiftStartsAt: string
  now: Date
}) => {
  const { colors } = useAppTheme()
  const startsAt = new Date(shiftStartsAt)
  const msRemaining = startsAt.getTime() - now.getTime()
  const isUrgent = msRemaining > 0 && msRemaining < ONE_HOUR_MS

  return (
    <Flex alignItems="center" gap={1}>
      <Text title={formatTimestampWithTimezone(startsAt)}>
        {formatDistanceStrict(startsAt, now, { addSuffix: true })}
      </Text>
      {isUrgent ? (
        <Tooltip content={SHIFT_STARTS_URGENT_TOOLTIP}>
          <Box flexShrink={0}>
            <TriangleAlert size={14} color={colors.warning} />
          </Box>
        </Tooltip>
      ) : null}
    </Flex>
  )
}

const buildColumns = (
  now: Date,
  positionId: number
): ColumnDef<AssessmentRow>[] => [
  {
    header: 'Worker',
    accessorKey: 'workerName',
    cell: ({ row }) => (
      <Text>
        {row.original.workerName || 'Unknown'} (
        <Link
          to={ROUTES.USER_DETAIL.replace(
            ':userId',
            row.original.workerId.toString()
          )}
          target="_blank"
        >
          {row.original.workerId}
        </Link>
        )
      </Text>
    ),
  },
  {
    header: ShiftStartsHeader,
    accessorKey: 'shiftStartsAt',
    cell: ({ row }) => {
      const value = row.original.shiftStartsAt
      if (!value) return '—'
      return <ShiftStartsCell shiftStartsAt={value} now={now} />
    },
  },
  {
    header: 'Assessment',
    id: 'assessmentLink',
    cell: ({ row }) => (
      <Link
        to={ROUTES.WORKER_ASSESSMENT_DETAIL.replace(
          ':positionId',
          positionId.toString()
        ).replace(':userId', row.original.workerId.toString())}
      >
        View
      </Link>
    ),
  },
]

export const PendingApprovalsTable = ({
  table,
  positionId,
  onLoadMore,
}: PendingApprovalsTableProps) => {
  const {
    items,
    isPending,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    isError,
    errorMessage,
  } = table
  const now = new Date()
  const columns = buildColumns(now, positionId)

  if (isPending) {
    return <Loading />
  }

  if (isError && items.length === 0) {
    return (
      <Message variant="error">
        {errorMessage || 'Failed to load pending approvals. Please try again.'}
      </Message>
    )
  }

  if (items.length === 0) {
    return (
      <TableEmptyState
        isFetching={isFetching}
        emptyMessage="No pending approvals found"
        emptyStateHeight={50}
      />
    )
  }

  return (
    <Box>
      {isError && errorMessage ? (
        <Box mb={2}>
          <Message variant="error">{errorMessage}</Message>
        </Box>
      ) : null}
      <Table columns={columns} data={items} textVerticalAlign="top" />
      {hasNextPage ? (
        <Flex mt={3}>
          <Button
            variant="secondary"
            loading={isFetchingNextPage || isFetching}
            onClick={onLoadMore}
          >
            Load more
          </Button>
        </Flex>
      ) : null}
    </Box>
  )
}
